// Copyright (c) Ping. All rights reserved.
//
// Re-invite-after-removal recovery.
//
// When B is removed from a group and later re-invited, B's client is stuck: the
// SDK dropped B's MLS group on eviction, yet B's cursor for that conversation is
// still ADVANCED (past the removal), so the BE only replays envelopes newer than
// it and `subscribeToConversations` (which only re-requests from-zero for a
// null/'' cursor) never actively re-fetches the re-invite Welcome. Result: B
// never re-joins, never sees the `com.ping.invite`, and the conversation looks
// frozen.
//
// Fix: on the reconcile, for every conversation the SERVER confirms B is a member
// of but the SDK holds NO group for, force a from-zero replay (delete the cursor
// via `transport.resetConversationForRejoin`) so the SDK rebuilds the full
// join → remove → re-Welcome sequence and settles on a clean re-join. We only act
// when a stale `mls.conv.<id>` mapping exists (B was a member before) — a
// brand-new conversation has no mapping and the normal unjoined replay handles
// it — and only ONCE per conversation per session, to avoid churn if a genuinely
// stuck conversation never joins.

import { log } from '@core/log/log';
import { bytesToHex } from '@core/network/deviceKeys';
import { db } from '@core/persistence/db';

import { kvAdapter } from './mlsConversation';

interface ListConvClient {
  listConversations?(): Promise<Array<{ id: Uint8Array }>>;
}

interface RejoinTransport {
  resetConversationForRejoin(conversationIdHex: string): Promise<void>;
}

// Session-scoped: reset each stuck conversation at most once (a second reset
// wouldn't help an SDK/BE-level stuck-join and would re-replay the whole log).
const resetThisSession = new Set<string>();

// Grace window after a local leave during which we must NOT clear `leftAt` from
// a stale server roster. Right after `markConversationLeft`, the server-side
// roster drop and the SDK's own self-remove Commit have not converged: the
// server still lists us as a member and the SDK still holds the group. Clearing
// `leftAt` in that window resurrected the group the user just left (the "left
// group reappears" bug). A genuine RE-INVITE always lands well after this
// window, so it still un-gates correctly.
const REJOIN_LEFT_CLEAR_GRACE_MS = 2 * 60 * 1000;

export async function recoverRemovedRejoins(params: {
  readonly client: unknown;
  readonly transport: RejoinTransport | null;
  readonly serverMemberIdHexes: readonly string[];
}): Promise<void> {
  const { client, transport, serverMemberIdHexes } = params;
  if (transport === null || serverMemberIdHexes.length === 0) return;
  const c = client as ListConvClient;
  if (typeof c.listConversations !== 'function') return;

  let sdkHas: Set<string>;
  try {
    const convs = await c.listConversations();
    sdkHas = new Set(convs.map((m) => bytesToHex(m.id).toLowerCase()));
  } catch {
    return; // SDK not ready yet — try again next tick.
  }

  for (const idHex of serverMemberIdHexes) {
    const lc = idHex.toLowerCase();
    if (sdkHas.has(lc)) {
      // Joined (or re-joined) — clear the "left/removed" gate + the reset latch.
      resetThisSession.delete(lc);
      try {
        const row = await db.conversations.get(lc);
        const leftAt = row !== undefined ? ((row as { leftAt?: number }).leftAt ?? 0) : 0;
        // Only un-gate a leave that is NOT fresh — see REJOIN_LEFT_CLEAR_GRACE_MS.
        if (leftAt > 0 && Date.now() - leftAt >= REJOIN_LEFT_CLEAR_GRACE_MS) {
          await db.conversations.update(lc, { leftAt: 0 } as never);
        }
      } catch {
        /* best-effort un-gate */
      }
      continue;
    }
    if (resetThisSession.has(lc)) continue;

    // Only recover an ESTABLISHED group we were genuinely a member of before:
    // an existing group row with a real roster, plus a stale `mls.conv.<id>`
    // mapping. A brand-new first join (no established row / no history) is left
    // to the normal unjoined replay, so we never disturb an in-progress join.
    const row = await db.conversations.get(lc).catch(() => undefined);
    const isEstablishedGroup =
      row !== undefined && row.kind === 'group' && (row.memberAccountIds?.length ?? 0) > 0;
    if (!isEstablishedGroup) continue;

    let hadMapping = false;
    try {
      hadMapping = (await kvAdapter.get(`mls.conv.${lc}`)) != null;
    } catch {
      hadMapping = false;
    }
    if (!hadMapping) continue;

    resetThisSession.add(lc);
    await transport.resetConversationForRejoin(lc);
    log.info({ event: 'rejoin.recovery.reset', conversationIdPrefix: lc.slice(0, 8) });
  }
}
